import { View, Text, TouchableOpacity, Animated } from "react-native";
import React, { useState, useEffect, useRef } from "react";
import { LinearGradient } from "expo-linear-gradient";

import { AntDesign } from "@expo/vector-icons";
import GlobalStyles from "../../Resources/Styles";
import Colors from "../../Resources/Color";
import { useScreenDimensions, useDevice } from "../_layout";

const AddRow = ({
  action,
  blockAdd,
  isLastInRow,
  shouldHighlight = false,
  onHighlightDismiss,
}) => {
  const { screenSize } = useScreenDimensions();
  const { isMobile } = useDevice();

  const [isHovered, setIsHovered] = useState(false);

  // Animation values for pulsing effect
  const pulseAnim = useRef(new Animated.Value(1)).current;
  const glowAnim = useRef(new Animated.Value(0)).current;
  const shimmerAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (shouldHighlight) {
      // Start pulsing animation
      const pulseAnimation = Animated.loop(
        Animated.sequence([
          Animated.timing(pulseAnim, {
            toValue: 1.03,
            duration: 1200,
            useNativeDriver: true,
          }),
          Animated.timing(pulseAnim, {
            toValue: 1,
            duration: 1200,
            useNativeDriver: true,
          }),
        ])
      );

      const glowAnimation = Animated.loop(
        Animated.sequence([
          Animated.timing(glowAnim, {
            toValue: 1,
            duration: 1200,
            useNativeDriver: false,
          }),
          Animated.timing(glowAnim, {
            toValue: 0,
            duration: 1200,
            useNativeDriver: false,
          }),
        ])
      );

      const shimmerAnimation = Animated.loop(
        Animated.sequence([
          Animated.delay(500),
          Animated.timing(shimmerAnim, {
            toValue: 1,
            duration: 1500,
            useNativeDriver: false,
          }),
          Animated.delay(2500),
          Animated.timing(shimmerAnim, {
            toValue: 0,
            duration: 0,
            useNativeDriver: false,
          }),
        ])
      );

      pulseAnimation.start();
      glowAnimation.start();
      shimmerAnimation.start();

      return () => {
        pulseAnimation.stop();
        glowAnimation.stop();
        shimmerAnimation.stop();
      };
    } else {
      // Reset animations
      pulseAnim.setValue(1);
      glowAnim.setValue(0);
      shimmerAnim.setValue(0);
    }
  }, [shouldHighlight, pulseAnim, glowAnim, shimmerAnim]);

  const handlePress = () => {
    if (shouldHighlight && onHighlightDismiss) {
      onHighlightDismiss();
    }
    action();
  };

  const glowColor = glowAnim.interpolate({
    inputRange: [0, 1],
    outputRange: ["rgba(63, 123, 141, 0)", "rgba(63, 123, 141, 0.25)"],
  });

  const shimmerTranslate = shimmerAnim.interpolate({
    inputRange: [0, 1],
    outputRange: ["-200%", "500%"],
  });

  return (
    <Animated.View
      style={{
        width: blockAdd
          ? "100%"
          : screenSize === "large"
          ? "100%"
          : `calc((100% - ${screenSize === "small" ? 16 : 48}px) / ${
              screenSize === "small" ? 3 : 5
            })`,
        paddingRight: blockAdd
          ? screenSize === "large"
            ? 54
            : screenSize === "medium"
            ? 40
            : 30
          : 0,
        maxWidth: screenSize != "medium" ? "100%" : "50%",
        opacity: isHovered ? 0.5 : 1,
        transform: shouldHighlight ? [{ scale: pulseAnim }] : [{ scale: 1 }],
      }}
      onMouseEnter={() => !isMobile && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {shouldHighlight && (
        <Animated.View
          style={{
            position: "absolute",
            top: -4,
            left: -4,
            right: blockAdd
              ? screenSize === "large"
                ? 50
                : screenSize === "medium"
                ? 36
                : 26
              : -4,
            bottom: -4,
            borderRadius: 12,
            backgroundColor: glowColor,
            zIndex: -1,
          }}
        />
      )}
      <TouchableOpacity
        onPress={handlePress}
        activeOpacity={0.7}
        style={[
          GlobalStyles.touchableRow,
          {
            backgroundColor: shouldHighlight
              ? Colors.bubbleColor
              : Colors.backgroundColor,
            justifyContent: "center",
            alignItems: "center",
            borderColor: shouldHighlight
              ? Colors.bubbleColor
              : Colors.grayColor,
            borderWidth: 1,
            shadowColor: shouldHighlight ? Colors.bubbleColor : "transparent",
            shadowOffset: {
              width: 0,
              height: 2,
            },
            shadowOpacity: shouldHighlight ? 0.25 : 0,
            shadowRadius: 3.84,
            elevation: shouldHighlight ? 5 : 0,
            overflow: "hidden",
          },
        ]}
      >
        {shouldHighlight && (
          <Animated.View
            style={{
              position: "absolute",
              top: 0,
              bottom: 0,
              width: "50%",
              transform: [
                { translateX: shimmerTranslate },
                { skewX: "-20deg" },
              ],
              zIndex: 1,
            }}
          >
            <LinearGradient
              colors={[
                "rgba(255, 255, 255, 0)",
                "rgba(255, 255, 255, 0.1)",
                "rgba(255, 255, 255, 0)",
              ]}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 0 }}
              style={{ flex: 1 }}
            />
          </Animated.View>
        )}
        <View style={{ zIndex: 2 }}>
          <AntDesign
            name="plus"
            size={22}
            color={shouldHighlight ? Colors.whiteColor : Colors.grayColor}
          />
        </View>
      </TouchableOpacity>
    </Animated.View>
  );
};

export default AddRow;
